import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { StocManualCard } from '../../components/orders/StocManualCard'
import { ProduseDesincronizate } from '../../components/orders/ProduseDesincronizate'

interface Settings {
  discountApprovalThresholdPct: string
}
interface EaCfg {
  baseUrl: string | null
  configured: boolean
  stockBaseUrl: string | null
  stockConfigured: boolean
  stockLocId: string | null
  stockAsOf: string | null
  branchConfigured: boolean
  ordersReadConfigured: boolean
  ordersReadSrc: string | null
  webFeedConfigured: boolean
  itemsConfigured: boolean
  partnersConfigured: boolean
}
interface ImportResult {
  total: number
  upserted: number
  skipped: number
}
interface SyncStateT {
  status: 'running' | 'done' | 'error'
  dryRun: boolean
  startedAt: string
  finishedAt?: string
  clients: number
  processed: number
  totalUpserted: number
  totalDeleted: number
  skipped: { clientId: number; outcome: string; note?: string }[]
  error?: string
}

/** Cheile integrarilor EA — determina ce modal de configurare se deschide. */
type CfgKey = 'new_order' | 'webfeed' | 'items' | 'partners' | 'stock' | 'partbranch' | 'orders'

/** Input etichetat reutilizat in modalele de configurare. */
function TInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-gray-600">{label}</span>
      <input
        className="w-full rounded border border-gray-300 px-3 py-2"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}

/** Pastila de stare pentru coloana „Stare” din tabelul de integrari. */
function StatusPill({ ok, extra }: { ok: boolean; extra?: string }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
          ok ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}
      >
        {ok ? '✓ configurat' : '— neconfigurat'}
      </span>
      {extra && <span className="text-xs text-gray-400">{extra}</span>}
    </span>
  )
}

export function SetariComenziPage() {
  const canManage = useAuthStore((s) => s.permissions.includes('orders:approve'))
  // Stocul manual se scrie cu `orders:write` — aceeasi permisiune ca ruta, ca ecranul sa nu
  // arate butoane care iau 403.
  const poateScrieStoc = useAuthStore((s) => s.permissions.includes('orders:write'))
  const qc = useQueryClient()
  const [threshold, setThreshold] = useState('15')
  const fileRef = useRef<HTMLInputElement>(null)
  const [cfg, setCfg] = useState<CfgKey | null>(null)
  const [eaUrl, setEaUrl] = useState('')
  const [eaT1, setEaT1] = useState('')
  const [eaT2, setEaT2] = useState('')
  const [stUrl, setStUrl] = useState('')
  const [stT1, setStT1] = useState('')
  const [stT2, setStT2] = useState('')
  const [stLoc, setStLoc] = useState('')
  const [branchResult, setBranchResult] = useState<string>('')
  const [probeSrc, setProbeSrc] = useState('orders')
  const [probeQ, setProbeQ] = useState('')
  const [brT1, setBrT1] = useState('')
  const [brT2, setBrT2] = useState('')
  const [wfT1, setWfT1] = useState('')
  const [wfT2, setWfT2] = useState('')
  const [itT1, setItT1] = useState('')
  const [itT2, setItT2] = useState('')
  const [paT1, setPaT1] = useState('')
  const [paT2, setPaT2] = useState('')
  const [probePartner, setProbePartner] = useState('')
  /** SKU optional: aduce randul BRUT al articolului, cu toate coloanele trimise de EA. */
  const [probeSku, setProbeSku] = useState('')
  const [pricesResult, setPricesResult] = useState('')
  const [itemsResult, setItemsResult] = useState('')
  const [mapResult, setMapResult] = useState('')
  const [orT1, setOrT1] = useState('')
  const [orT2, setOrT2] = useState('')
  const [orSrc, setOrSrc] = useState('orders')
  const [ordersResult, setOrdersResult] = useState('')

  const { data } = useQuery({
    queryKey: ['order-settings'],
    queryFn: async () => (await api.get<{ settings: Settings }>('/orders/settings')).data.settings,
  })
  useEffect(() => {
    if (data) setThreshold(String(Number(data.discountApprovalThresholdPct)))
  }, [data])

  const { data: eaCfg } = useQuery({
    queryKey: ['orders-ea-settings'],
    queryFn: async () => (await api.get<EaCfg>('/orders/ea-settings')).data,
  })
  useEffect(() => {
    if (eaCfg?.baseUrl) setEaUrl(eaCfg.baseUrl)
    if (eaCfg?.stockBaseUrl) setStUrl(eaCfg.stockBaseUrl)
    if (eaCfg?.stockLocId) setStLoc(eaCfg.stockLocId)
    if (eaCfg?.ordersReadSrc) setOrSrc(eaCfg.ordersReadSrc)
  }, [eaCfg])

  const ordersCfgMut = useMutation({
    mutationFn: async () =>
      (await api.put('/orders/orders-read-settings', { t1: orT1, t2: orT2, src: orSrc || null }))
        .data,
    onSuccess: () => {
      toast.success('Config orders (Var1=orders) salvat')
      setOrT1('')
      setOrT2('')
      qc.invalidateQueries({ queryKey: ['orders-ea-settings'] })
    },
    onError: () => toast.error('Eroare la salvare config orders'),
  })
  const rawErr = (e: unknown) =>
    (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
  const errMsg = (e: unknown) => rawErr(e) || 'necunoscuta'
  const pricesCfgMut = useMutation({
    mutationFn: async () =>
      (
        await api.put('/orders/prices-settings', {
          webFeedT1: wfT1 || null,
          webFeedT2: wfT2 || null,
          itemsT1: itT1 || null,
          itemsT2: itT2 || null,
          partnersT1: paT1 || null,
          partnersT2: paT2 || null,
        })
      ).data,
    onSuccess: () => {
      toast.success('Tokeni prețuri salvați')
      setWfT1('')
      setWfT2('')
      setItT1('')
      setItT2('')
      setPaT1('')
      setPaT2('')
      qc.invalidateQueries({ queryKey: ['orders-ea-settings'] })
    },
    onError: (e: unknown) => toast.error('Eroare la salvare: ' + errMsg(e)),
  })
  const probeWebFeedMut = useMutation({
    mutationFn: async () =>
      (
        await api.get(
          // `sku` e optional: cand e dat, raspunsul contine randul BRUT al acelui articol, cu
          // toate coloanele trimise de EA. Asa se vede daca feed-ul spune ceva despre existenta
          // unei reguli de pret, dincolo de pretul in sine.
          `/orders/probe-webfeed?partner=${encodeURIComponent(probePartner)}` +
            (probeSku.trim() ? `&sku=${encodeURIComponent(probeSku.trim())}` : ''),
        )
      ).data,
    onSuccess: (d) => setPricesResult(JSON.stringify(d, null, 2)),
    onError: (e: unknown) => setPricesResult('Eroare: ' + errMsg(e)),
  })
  const syncPricesMut = useMutation({
    mutationFn: async (dryRun: boolean) =>
      (await api.post(`/orders/sync-prices${dryRun ? '?dryRun=1' : ''}`)).data,
    onSuccess: (r: { dryRun: boolean }) => {
      toast.success(
        `${r.dryRun ? 'Simulare' : 'Sincronizare'} pornită în fundal. Progresul apare sub tabel (~7 min).`,
      )
      qc.invalidateQueries({ queryKey: ['sync-prices-state'] })
    },
    onError: (e: unknown) => toast.error('Nu am putut porni: ' + errMsg(e)),
  })
  // Progresul/rezultatul sincronizarii — poll la 3s cat timp ruleaza.
  const { data: syncState } = useQuery<{ state: SyncStateT | null }>({
    queryKey: ['sync-prices-state'],
    queryFn: async () => (await api.get('/orders/sync-prices/state')).data,
    refetchInterval: (q) =>
      (q.state.data as { state: SyncStateT | null } | undefined)?.state?.status === 'running'
        ? 3000
        : false,
  })
  const syncItemsMut = useMutation({
    mutationFn: async () =>
      (await api.post('/orders/sync-items', undefined, { timeout: 300_000 })).data.result,
    onSuccess: (r: { fetched: number; updated: number; unknownSkus: string[] }) => {
      toast.success(
        `Catalog: ${r.updated} produse actualizate din ${r.fetched} (${r.unknownSkus.length} SKU noi, neimportate)`,
      )
      setItemsResult(JSON.stringify(r, null, 2))
    },
    onError: (e: unknown) => setItemsResult('Eroare: ' + errMsg(e)),
  })
  /*
    Proba pe exportul de catalog. Nu scrie nimic: intoarce coloanele brute ale primului rand.

    De ce e nevoie: am adaugat scrierea lui `item_type` din `typename`, s-a sincronizat, si campul
    a ramas gol. Deci exportul de catalog nu trimite acea coloana — desi feed-ul de preturi o are.
    Cele doua exporturi au fiecare propriul set de coloane, configurat in EA (Var2).
  */
  const probeItemsMut = useMutation({
    mutationFn: async () =>
      (await api.post('/orders/sync-items?probe=1', undefined, { timeout: 120_000 })).data,
    onSuccess: (r: unknown) => setItemsResult(JSON.stringify(r, null, 2)),
    onError: (e: unknown) => setItemsResult('Eroare: ' + errMsg(e)),
  })

  const mapPartnersMut = useMutation({
    mutationFn: async () =>
      (await api.post('/orders/map-partners', undefined, { timeout: 120_000 })).data.result,
    onSuccess: (r: {
      eligible: number
      matched: number
      noCif: unknown[]
      unmatched: unknown[]
    }) => {
      toast.success(
        `Mapare: ${r.matched}/${r.eligible} clienți cu CIF potriviți (${r.noCif.length} fără CIF, ${r.unmatched.length} negăsiți în EA)`,
      )
      setMapResult(JSON.stringify(r, null, 2))
      qc.invalidateQueries({ queryKey: ['unmapped-partners'] })
    },
    onError: (e: unknown) => setMapResult('Eroare: ' + errMsg(e)),
  })
  const { data: unmapped } = useQuery({
    queryKey: ['unmapped-partners'],
    queryFn: async () =>
      (
        await api.get<{ clients: { id: number; name: string; cif: string | null }[] }>(
          '/orders/unmapped-partners',
        )
      ).data.clients,
  })
  const discoverOrdersMut = useMutation({
    mutationFn: async () =>
      (await api.get(`/orders/orders-read-discover?src=${encodeURIComponent(orSrc)}`)).data,
    onSuccess: (d) => setOrdersResult(JSON.stringify(d, null, 2)),
    onError: (e: unknown) => setOrdersResult('Eroare: ' + errMsg(e)),
  })
  const syncOrdersMut = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ result: { fetched: number; partners: number } }>(
          '/orders/sync-orders',
          undefined,
          { timeout: 300_000 },
        )
      ).data.result,
    onSuccess: (r) =>
      toast.success(`Comenzi EA: ${r.fetched} rânduri, ${r.partners} parteneri cu ultima comandă`),
    onError: (e: unknown) => toast.error('Sincronizarea a eșuat: ' + errMsg(e)),
  })

  const stockCfgMut = useMutation({
    mutationFn: async () =>
      (
        await api.put('/orders/stock-settings', {
          baseUrl: stUrl,
          t1: stT1,
          t2: stT2,
          locId: stLoc || null,
        })
      ).data,
    onSuccess: () => {
      toast.success('Config get_stock salvat')
      setStT1('')
      setStT2('')
      qc.invalidateQueries({ queryKey: ['orders-ea-settings'] })
    },
    onError: () => toast.error('Eroare la salvare config stoc'),
  })

  const discoverBranchMut = useMutation({
    mutationFn: async () => (await api.get('/orders/branches/discover')).data,
    onSuccess: (d) => setBranchResult(JSON.stringify(d, null, 2)),
    onError: (e: unknown) => setBranchResult('Eroare: ' + errMsg(e)),
  })

  const probeMut = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams()
      if (probeSrc) params.set('src', probeSrc)
      if (probeQ) params.set('q', probeQ)
      return (await api.get(`/orders/branches/discover?${params.toString()}`)).data
    },
    onSuccess: (d) => setBranchResult(JSON.stringify(d, null, 2)),
    onError: (e: unknown) => setBranchResult('Eroare: ' + errMsg(e)),
  })

  const branchCfgMut = useMutation({
    mutationFn: async () => (await api.put('/orders/branch-settings', { t1: brT1, t2: brT2 })).data,
    onSuccess: () => {
      toast.success('Tokeni partbranch salvați')
      setBrT1('')
      setBrT2('')
      qc.invalidateQueries({ queryKey: ['orders-ea-settings'] })
    },
    onError: () => toast.error('Eroare la salvare tokeni'),
  })

  const syncBranchesMut = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          result: {
            fetched: number
            upserted: number
            linked: number
            unlinked: number
            adopted: number
            merged: number
          }
        }>('/orders/sync-branches', undefined, { timeout: 180_000 })
      ).data.result,
    onSuccess: (r) => {
      const extra = [
        r.adopted ? `${r.adopted} create în CRM au primit id-ul din EA` : '',
        r.merged ? `${r.merged} duplicate eliminate` : '',
      ].filter(Boolean)
      toast.success(
        `Puncte de lucru: ${r.upserted} sincronizate, ${r.linked} legate de clienți (${r.unlinked} fără client în CRM)` +
          (extra.length ? ` · ${extra.join(', ')}` : ''),
      )
    },
    onError: (e: unknown) => toast.error(rawErr(e) || 'Eroare la sincronizare puncte de lucru'),
  })

  const syncImagesMut = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          result: { shopProducts: number; skusWithImage: number; updated: number }
        }>('/orders/sync-images')
      ).data.result,
    onSuccess: (r) =>
      toast.success(
        `Poze sincronizate: ${r.updated} produse (din ${r.skusWithImage} SKU-uri cu poză)`,
      ),
    onError: (e: unknown) => toast.error(rawErr(e) || 'Eroare la sincronizare poze'),
  })

  const syncStockMut = useMutation({
    mutationFn: async () =>
      (await api.post<{ result: { updated: number; total: number } }>('/orders/sync-stock')).data
        .result,
    onSuccess: (r) => toast.success(`Stoc sincronizat: ${r.updated} din ${r.total} articole`),
    onError: (e: unknown) => toast.error(rawErr(e) || 'Eroare la sincronizare stoc'),
  })

  const eaMut = useMutation({
    mutationFn: async () =>
      (await api.put('/orders/ea-settings', { baseUrl: eaUrl, t1: eaT1, t2: eaT2 })).data,
    onSuccess: () => {
      toast.success('Config ExpertAccounts salvat')
      setEaT1('')
      setEaT2('')
      qc.invalidateQueries({ queryKey: ['orders-ea-settings'] })
    },
    onError: () => toast.error('Eroare la salvare config EA'),
  })

  const saveMut = useMutation({
    mutationFn: async () =>
      (await api.put('/orders/settings', { discountApprovalThresholdPct: Number(threshold) })).data,
    onSuccess: () => {
      toast.success('Prag salvat')
      qc.invalidateQueries({ queryKey: ['order-settings'] })
    },
    onError: () => toast.error('Eroare la salvare'),
  })

  const importMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return (await api.post<{ result: ImportResult }>('/orders/price-rules/import', fd)).data
        .result
    },
    onSuccess: (r) => {
      toast.success(`Import reguli: ${r.upserted} din ${r.total} (${r.skipped} sărite)`)
      qc.invalidateQueries({ queryKey: ['orders-clients'] })
      if (fileRef.current) fileRef.current.value = ''
    },
    onError: () => toast.error('Eroare la import'),
  })

  if (!canManage) {
    return (
      <div className="max-w-2xl space-y-4">
        <h1 className="text-xl font-semibold">Setări → Comenzi</h1>
        <Card>
          <p className="text-sm text-b4l-amber-dark">
            Nu ai acces la configurarea comenzilor. Cere unui administrator dreptul
            <strong> orders:approve</strong>.
          </p>
        </Card>
      </div>
    )
  }

  const stockExtra =
    eaCfg?.stockConfigured && eaCfg.stockAsOf
      ? `sync ${new Date(eaCfg.stockAsOf).toLocaleString('ro-RO')}`
      : undefined
  const ph = (configured?: boolean) => (configured ? '•••••• (nemodificat)' : '')

  /** Titlul + conținutul modalului de configurare, în funcție de integrarea aleasă. */
  const cfgTitle: Record<CfgKey, string> = {
    new_order: 'Trimitere comenzi — API new_order',
    webfeed: 'Prețuri per partener — Var1 = sqlItemsWebFeed()',
    items: 'Catalog articole — Var1 = items',
    partners: 'Parteneri (mapare pe CIF) — Var1 = partners',
    stock: 'Stoc — API get_stock',
    partbranch: 'Puncte de lucru — Var1 = partbranch',
    orders: 'Ultima comandă — Var1 = orders',
  }

  function ConfigBody() {
    switch (cfg) {
      case 'new_order':
        return (
          <div className="space-y-3">
            <TInput
              label="URL de bază (fără ?api=...)"
              value={eaUrl}
              onChange={setEaUrl}
              placeholder="https://erp.diverseo.expert-account.com/"
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <TInput
                label="Token t1"
                value={eaT1}
                onChange={setEaT1}
                placeholder={ph(eaCfg?.configured)}
              />
              <TInput
                label="Token t2"
                value={eaT2}
                onChange={setEaT2}
                placeholder={ph(eaCfg?.configured)}
              />
            </div>
            <p className="text-xs text-gray-500">
              Tokenii se stochează criptat. După configurare, butonul „Trimite în EA” devine activ
              pe comenzile aprobate.
            </p>
          </div>
        )
      case 'webfeed':
        return (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <TInput
                label="Token t1"
                value={wfT1}
                onChange={setWfT1}
                placeholder={ph(eaCfg?.webFeedConfigured)}
              />
              <TInput
                label="Token t2"
                value={wfT2}
                onChange={setWfT2}
                placeholder={ph(eaCfg?.webFeedConfigured)}
              />
            </div>
            <div className="border-t pt-3">
              <p className="mb-1 text-sm font-medium text-gray-700">Probă (nu scrie nimic)</p>
              <p className="mb-2 text-xs text-gray-500">
                Un partener cu prețuri negociate trebuie să întoarcă un număr &gt; 0. Zero înseamnă,
                cel mai probabil, nume greșit: EA nu dă eroare, ci întoarce prețurile de catalog.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <label className="block flex-1">
                  <span className="mb-1 block text-sm text-gray-600">
                    Nume partener (exact, din EA)
                  </span>
                  <input
                    className="w-full rounded border border-gray-300 px-3 py-2"
                    placeholder="Remedium Farm Srl"
                    value={probePartner}
                    onChange={(e) => setProbePartner(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm text-gray-600">Cod articol (opțional)</span>
                  <input
                    className="w-full rounded border border-gray-300 px-3 py-2"
                    placeholder="CRMZ300-P750"
                    value={probeSku}
                    onChange={(e) => setProbeSku(e.target.value)}
                  />
                </label>
                <Button
                  variant="secondary"
                  disabled={!probePartner.trim()}
                  onClick={() => probeWebFeedMut.mutate()}
                  loading={probeWebFeedMut.isPending}
                >
                  Testează
                </Button>
              </div>
              {pricesResult && (
                <pre className="mt-3 max-h-64 overflow-auto rounded bg-gray-50 p-3 text-xs">
                  {pricesResult}
                </pre>
              )}
            </div>
          </div>
        )
      case 'items':
        return (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <TInput
                label="Token t1"
                value={itT1}
                onChange={setItT1}
                placeholder={ph(eaCfg?.itemsConfigured)}
              />
              <TInput
                label="Token t2"
                value={itT2}
                onChange={setItT2}
                placeholder={ph(eaCfg?.itemsConfigured)}
              />
            </div>
            <div className="border-t pt-3">
              <p className="mb-2 text-xs text-gray-500">
                Aduce catalogul de articole (listPrice + TVA). SKU-urile necunoscute apar mai jos
                (nu se importă automat).
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  disabled={!eaCfg?.itemsConfigured}
                  onClick={() => syncItemsMut.mutate()}
                  loading={syncItemsMut.isPending}
                >
                  Sincronizează catalog
                </Button>
                {/*
                  Proba, langa rezultatul ei. Butonul era in tabel, dar `itemsResult` se afiseaza
                  doar aici, in fereastra de configurare — apasat de acolo, rula si raspunsul cadea
                  intr-un panou inchis. Un diagnostic al carui raspuns nu se vede nu e diagnostic.
                */}
                <Button
                  variant="secondary"
                  disabled={!eaCfg?.itemsConfigured}
                  onClick={() => probeItemsMut.mutate()}
                  loading={probeItemsMut.isPending}
                >
                  Probă — ce coloane trimite EA
                </Button>
              </div>
              {itemsResult && (
                <pre className="mt-3 max-h-64 overflow-auto rounded bg-gray-50 p-3 text-xs">
                  {itemsResult}
                </pre>
              )}
            </div>
          </div>
        )
      case 'partners':
        return (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <TInput
                label="Token t1"
                value={paT1}
                onChange={setPaT1}
                placeholder={ph(eaCfg?.partnersConfigured)}
              />
              <TInput
                label="Token t2"
                value={paT2}
                onChange={setPaT2}
                placeholder={ph(eaCfg?.partnersConfigured)}
              />
            </div>
            <div className="border-t pt-3">
              <p className="mb-1 text-sm font-medium text-gray-700">
                Mapare clienți → parteneri EA (pe CIF)
              </p>
              <p className="mb-2 text-xs text-gray-500">
                Rezolvă numele exact al fiecărui client în EA, pe baza CIF-ului. De obicei se face
                automat la sincronizarea orară a portofoliilor; rulează manual doar pentru
                cazuri-limită (nume diferit sau fără CIF).
              </p>
              <Button
                variant="secondary"
                onClick={() => mapPartnersMut.mutate()}
                loading={mapPartnersMut.isPending}
              >
                Rulează maparea
              </Button>
              {mapResult && (
                <pre className="mt-3 max-h-48 overflow-auto rounded bg-gray-50 p-3 text-xs">
                  {mapResult}
                </pre>
              )}
              {unmapped && unmapped.length > 0 && (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-xs">
                  <p className="mb-1 font-medium text-amber-800">
                    {unmapped.length} client(i) cu reguli de preț dar fără mapare EA — necesită
                    rezolvare manuală:
                  </p>
                  <ul className="list-inside list-disc text-amber-700">
                    {unmapped.map((c) => (
                      <li key={c.id}>
                        {c.name} {c.cif ? `(CIF ${c.cif})` : '(fără CIF)'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )
      case 'stock':
        return (
          <div className="space-y-3">
            <TInput
              label="URL de bază (fără ?api=...)"
              value={stUrl}
              onChange={setStUrl}
              placeholder="https://erp.diverseo.expert-account.com/"
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <TInput
                label="Token t1"
                value={stT1}
                onChange={setStT1}
                placeholder={ph(eaCfg?.stockConfigured)}
              />
              <TInput
                label="Token t2"
                value={stT2}
                onChange={setStT2}
                placeholder={ph(eaCfg?.stockConfigured)}
              />
            </div>
            <TInput
              label="Locație / gestiune ID (ex. Depozit) — gol = default EA"
              value={stLoc}
              onChange={setStLoc}
              placeholder="ex. 2"
            />
            <p className="text-xs text-gray-500">
              Stocul disponibil (minus comenzile aprobate) apare la fiecare produs în comandă.
            </p>
          </div>
        )
      case 'partbranch':
        return (
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
              Endpoint separat: cel de parteneri (Var1=partners) NU poate citi partbranch. Creează
              în EA un API-call get_data cu <b>Var1=partbranch</b> și folosește tokenii lui aici.
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <TInput
                label="Token t1 (partbranch)"
                value={brT1}
                onChange={setBrT1}
                placeholder={ph(eaCfg?.branchConfigured)}
              />
              <TInput
                label="Token t2 (partbranch)"
                value={brT2}
                onChange={setBrT2}
                placeholder={ph(eaCfg?.branchConfigured)}
              />
            </div>
            <p className="text-xs text-gray-500">
              Se sincronizează automat, din oră în oră; butonul „Sincronizează” din tabel pornește
              sincronizarea imediat.
            </p>
          </div>
        )
      case 'orders':
        return (
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
              Endpoint separat (ca partbranch): creează în EA un API-call get_data cu{' '}
              <b>Var1=orders</b>. „Sursă” = numele trimis în apel (implicit <b>orders</b>); dacă
              tabela are alt nume, ajustează-l.
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <TInput
                label="Token t1 (orders)"
                value={orT1}
                onChange={setOrT1}
                placeholder={ph(eaCfg?.ordersReadConfigured)}
              />
              <TInput
                label="Token t2 (orders)"
                value={orT2}
                onChange={setOrT2}
                placeholder={ph(eaCfg?.ordersReadConfigured)}
              />
              <TInput label="Sursă (src)" value={orSrc} onChange={setOrSrc} placeholder="orders" />
            </div>
            <Button
              variant="secondary"
              onClick={() => discoverOrdersMut.mutate()}
              loading={discoverOrdersMut.isPending}
              disabled={!eaCfg?.ordersReadConfigured}
            >
              Vezi coloanele (discover)
            </Button>
            {ordersResult && (
              <pre className="max-h-64 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
                {ordersResult}
              </pre>
            )}
          </div>
        )
      default:
        return null
    }
  }

  function ConfigFooter(): ReactNode {
    const close = (
      <Button variant="ghost" onClick={() => setCfg(null)}>
        Închide
      </Button>
    )
    switch (cfg) {
      case 'new_order':
        return (
          <>
            {close}
            <Button
              onClick={() => eaMut.mutate()}
              loading={eaMut.isPending}
              disabled={!eaUrl || !eaT1 || !eaT2}
            >
              Salvează config EA
            </Button>
          </>
        )
      case 'webfeed':
      case 'items':
      case 'partners':
        return (
          <>
            {close}
            <Button onClick={() => pricesCfgMut.mutate()} loading={pricesCfgMut.isPending}>
              Salvează tokenii
            </Button>
          </>
        )
      case 'stock':
        return (
          <>
            {close}
            <Button
              onClick={() => stockCfgMut.mutate()}
              loading={stockCfgMut.isPending}
              disabled={!stUrl || (!eaCfg?.stockConfigured && (!stT1 || !stT2))}
            >
              Salvează config stoc
            </Button>
          </>
        )
      case 'partbranch':
        return (
          <>
            {close}
            <Button
              onClick={() => branchCfgMut.mutate()}
              loading={branchCfgMut.isPending}
              disabled={!brT1 || !brT2}
            >
              Salvează tokeni partbranch
            </Button>
          </>
        )
      case 'orders':
        return (
          <>
            {close}
            <Button
              onClick={() => ordersCfgMut.mutate()}
              loading={ordersCfgMut.isPending}
              disabled={!orT1 || !orT2}
            >
              Salvează tokeni orders
            </Button>
          </>
        )
      default:
        return close
    }
  }

  return (
    <div className="max-w-4xl space-y-4">
      <h1 className="text-xl font-semibold">Setări → Comenzi</h1>

      <Card
        title="Prag aprobare discount"
        description="Discountul (%) peste care o comandă trebuie aprobată de un utilizator cu dreptul „Aprobare comenzi” înainte de a fi trimisă"
      >
        <div className="flex items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-sm text-gray-600">Prag (%)</span>
            <input
              type="number"
              min={0}
              max={100}
              className="w-32 rounded border border-gray-300 px-3 py-2"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </label>
          <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending}>
            Salvează
          </Button>
        </div>
      </Card>

      <Card
        title="Integrări ExpertAccounts"
        description="Conexiuni către ExpertAccounts (get_exportdata / get_data / new_order). „Configurează” = adresă + tokeni; „Sincronizează” = rulare manuală (prețurile, catalogul, stocul, punctele de lucru și portofoliile se sincronizează și automat)."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">Integrare</th>
                <th className="py-2 pr-3 font-medium">Scop</th>
                <th className="py-2 pr-3 font-medium">Stare</th>
                <th className="py-2 text-right font-medium">Acțiuni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-b4l-border">
              <tr>
                <td className="py-2.5 pr-3 font-mono text-xs">new_order</td>
                <td className="py-2.5 pr-3 text-gray-600">Trimite comenzi în EA</td>
                <td className="py-2.5 pr-3">
                  <StatusPill ok={!!eaCfg?.configured} />
                </td>
                <td className="py-2.5">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      onClick={() => setCfg('new_order')}
                    >
                      Configurează
                    </Button>
                  </div>
                </td>
              </tr>

              <tr>
                <td className="py-2.5 pr-3 font-mono text-xs">sqlItemsWebFeed</td>
                <td className="py-2.5 pr-3 text-gray-600">Prețuri per partener</td>
                <td className="py-2.5 pr-3">
                  <StatusPill ok={!!eaCfg?.webFeedConfigured} />
                </td>
                <td className="py-2.5">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      onClick={() => setCfg('webfeed')}
                    >
                      Configurează
                    </Button>
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      disabled={!eaCfg?.webFeedConfigured}
                      onClick={() => syncPricesMut.mutate(true)}
                      loading={syncPricesMut.isPending}
                    >
                      Simulează
                    </Button>
                    <Button
                      className="px-2 py-1 text-xs"
                      disabled={!eaCfg?.webFeedConfigured}
                      onClick={() => syncPricesMut.mutate(false)}
                      loading={syncPricesMut.isPending}
                    >
                      Sincronizează prețuri
                    </Button>
                  </div>
                </td>
              </tr>

              <tr>
                <td className="py-2.5 pr-3 font-mono text-xs">items</td>
                <td className="py-2.5 pr-3 text-gray-600">Catalog articole (listPrice + TVA)</td>
                <td className="py-2.5 pr-3">
                  <StatusPill ok={!!eaCfg?.itemsConfigured} />
                </td>
                <td className="py-2.5">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      onClick={() => setCfg('items')}
                    >
                      Configurează
                    </Button>
                    <Button
                      className="px-2 py-1 text-xs"
                      disabled={!eaCfg?.itemsConfigured}
                      onClick={() => syncItemsMut.mutate()}
                      loading={syncItemsMut.isPending}
                    >
                      Sincronizează catalog
                    </Button>
                  </div>
                </td>
              </tr>

              <tr>
                <td className="py-2.5 pr-3 font-mono text-xs">partners</td>
                <td className="py-2.5 pr-3 text-gray-600">
                  Mapare pe CIF
                  {unmapped && unmapped.length > 0 && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                      {unmapped.length} de rezolvat
                    </span>
                  )}
                </td>
                <td className="py-2.5 pr-3">
                  <StatusPill ok={!!eaCfg?.partnersConfigured} />
                </td>
                <td className="py-2.5">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      onClick={() => setCfg('partners')}
                    >
                      Configurează
                    </Button>
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      disabled={!eaCfg?.partnersConfigured}
                      onClick={() => mapPartnersMut.mutate()}
                      loading={mapPartnersMut.isPending}
                    >
                      Mapare
                    </Button>
                  </div>
                </td>
              </tr>

              <tr>
                <td className="py-2.5 pr-3 font-mono text-xs">get_stock</td>
                <td className="py-2.5 pr-3 text-gray-600">Stoc disponibil</td>
                <td className="py-2.5 pr-3">
                  <StatusPill ok={!!eaCfg?.stockConfigured} extra={stockExtra} />
                </td>
                <td className="py-2.5">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      onClick={() => setCfg('stock')}
                    >
                      Configurează
                    </Button>
                    <Button
                      className="px-2 py-1 text-xs"
                      disabled={!eaCfg?.stockConfigured}
                      onClick={() => syncStockMut.mutate()}
                      loading={syncStockMut.isPending}
                    >
                      Sincronizează stoc
                    </Button>
                  </div>
                </td>
              </tr>

              <tr>
                <td className="py-2.5 pr-3 font-mono text-xs">partbranch</td>
                <td className="py-2.5 pr-3 text-gray-600">Puncte de lucru (sedii livrare)</td>
                <td className="py-2.5 pr-3">
                  <StatusPill ok={!!eaCfg?.branchConfigured} />
                </td>
                <td className="py-2.5">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      onClick={() => setCfg('partbranch')}
                    >
                      Configurează
                    </Button>
                    <Button
                      className="px-2 py-1 text-xs"
                      disabled={!eaCfg?.branchConfigured}
                      onClick={() => syncBranchesMut.mutate()}
                      loading={syncBranchesMut.isPending}
                    >
                      Sincronizează
                    </Button>
                  </div>
                </td>
              </tr>

              <tr>
                <td className="py-2.5 pr-3 font-mono text-xs">orders</td>
                <td className="py-2.5 pr-3 text-gray-600">Ultima comandă per partener</td>
                <td className="py-2.5 pr-3">
                  <StatusPill ok={!!eaCfg?.ordersReadConfigured} />
                </td>
                <td className="py-2.5">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      onClick={() => setCfg('orders')}
                    >
                      Configurează
                    </Button>
                    <Button
                      className="px-2 py-1 text-xs"
                      disabled={!eaCfg?.ordersReadConfigured}
                      onClick={() => syncOrdersMut.mutate()}
                      loading={syncOrdersMut.isPending}
                    >
                      Sincronizează
                    </Button>
                  </div>
                </td>
              </tr>

              <tr>
                <td className="py-2.5 pr-3 font-mono text-xs">Shopify</td>
                <td className="py-2.5 pr-3 text-gray-600">Poze produs (din boost4life.ro)</td>
                <td className="py-2.5 pr-3">
                  <span className="text-xs text-gray-400">—</span>
                </td>
                <td className="py-2.5">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      className="px-2 py-1 text-xs"
                      onClick={() => syncImagesMut.mutate()}
                      loading={syncImagesMut.isPending}
                    >
                      Sincronizează poze
                    </Button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {syncState?.state && (
          <div
            className={`mt-4 rounded border p-3 text-xs ${
              syncState.state.status === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : syncState.state.status === 'running'
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-green-200 bg-green-50 text-green-700'
            }`}
          >
            <p className="font-medium">
              {syncState.state.dryRun ? 'Simulare' : 'Sincronizare'} prețuri —{' '}
              {syncState.state.status === 'running'
                ? `în curs… ${syncState.state.processed}/${syncState.state.clients} clienți`
                : syncState.state.status === 'error'
                  ? 'eroare'
                  : 'terminată'}
            </p>
            {syncState.state.status !== 'error' && (
              <p className="mt-1">
                {syncState.state.totalUpserted} prețuri{' '}
                {syncState.state.dryRun ? 'ar fi scrise' : 'scrise'}, {syncState.state.totalDeleted}{' '}
                {syncState.state.dryRun ? 'ar fi șterse' : 'șterse'},{' '}
                {syncState.state.skipped.length} săriți
              </p>
            )}
            {syncState.state.error && <p className="mt-1">{syncState.state.error}</p>}
            {syncState.state.skipped.length > 0 && (
              <ul className="mt-1 list-inside list-disc">
                {syncState.state.skipped.slice(0, 10).map((s) => (
                  <li key={s.clientId}>
                    client #{s.clientId}: {s.outcome}
                    {s.note ? ` — ${s.note}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      <Card
        title="Reguli de preț (import Excel)"
        description="Reimport din Excel (oglinda tabelei EA price_rules). Idempotent — actualizează pe idpr."
      >
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="text-sm"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) importMut.mutate(f)
            }}
          />
          {importMut.isPending && <span className="text-sm text-gray-500">Se importă…</span>}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          La faza de integrare EA, regulile se vor sincroniza automat din ExpertAccounts.
        </p>
      </Card>

      <StocManualCard canWrite={poateScrieStoc} />
      <ProduseDesincronizate />

      <details className="rounded-lg border border-b4l-border bg-white shadow-sm">
        <summary className="cursor-pointer px-5 py-3 text-base font-semibold tracking-tight">
          Diagnostic EA (avansat)
        </summary>
        <div className="space-y-3 px-5 pb-5 pt-1">
          <p className="text-xs text-b4l-text-muted">
            Interoghează orice sursă EA (get_data) — vezi câmpurile și primele rânduri. Util pentru
            a găsi unde stochează EA punctul de lucru pe o comandă de test.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <TInput
              label="Sursă (src)"
              value={probeSrc}
              onChange={setProbeSrc}
              placeholder="ex. orders, order_details, partners_branch"
            />
            <TInput
              label="Filtru opțional (câmp=valoare)"
              value={probeQ}
              onChange={setProbeQ}
              placeholder="ex. order_refno=TEST1"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => probeMut.mutate()}
              loading={probeMut.isPending}
              disabled={!probeSrc}
            >
              Interoghează sursa
            </Button>
            <Button
              variant="secondary"
              onClick={() => discoverBranchMut.mutate()}
              loading={discoverBranchMut.isPending}
            >
              Auto-descoperă sucursale
            </Button>
          </div>
          {branchResult && (
            <pre className="max-h-96 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
              {branchResult}
            </pre>
          )}
        </div>
      </details>

      <Modal
        open={cfg !== null}
        onClose={() => setCfg(null)}
        title={cfg ? cfgTitle[cfg] : ''}
        size="lg"
        footer={ConfigFooter()}
      >
        {ConfigBody()}
      </Modal>
    </div>
  )
}
